import { t } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { PetType } from '@rover/types/src/Pet';

import { DateData } from './types';

export enum HeaderScenariosEnum {
  SELECTED_SITTER = 'selected-sitter',
  BEST_MATCHES = 'best-matches',
  SEARCH_RESULTS = 'search-results',
  LOW_SUPPLY = 'low-supply',
  NO_RESULTS = 'no-results',
}

export enum HeaderIconEnum {
  BOLT = 'NowBolt',
  EDIT = 'Edit',
  MAP = 'Map',
  PERSON = 'Person',
  SEARCH = 'Search',
}

// TITLES
export enum HeaderTitlesEnum {
  AVAILABLE_SITTERS_NEAR_YOU = 'available-sitters-near-you',
  FIND_MATCH = 'find-a-match',
  OTHER_SITTERS_IN_AREA = 'other-sitters-in-your-area',
  SITTERS_CLOSE_TO_YOU = 'sitters-close-to-you',
  SITTERS_IN_AREA = 'sitters-in-your-area',
  SELECTED_SITTER_TITLE = 'your-selected-sitter',
  SELECTED_SITTER_UNAVAILABLE_TITLE = 'your-selected-sitter-is-not-available',
  BEST_MATCHES = 'best-matches-for-your-request',
}

export const HEADER_TITLES = (): Record<HeaderTitlesEnum, string> => ({
  [HeaderTitlesEnum.AVAILABLE_SITTERS_NEAR_YOU]: t`Available sitters near you`,
  [HeaderTitlesEnum.FIND_MATCH]: t`Find a match`,
  [HeaderTitlesEnum.OTHER_SITTERS_IN_AREA]: t`Other sitters in your area`,
  [HeaderTitlesEnum.SITTERS_CLOSE_TO_YOU]: t`Sitters close to you`,
  [HeaderTitlesEnum.SITTERS_IN_AREA]: t`Sitters in your area`,
  [HeaderTitlesEnum.SELECTED_SITTER_TITLE]: t`Your selected sitter`,
  [HeaderTitlesEnum.SELECTED_SITTER_UNAVAILABLE_TITLE]: t`Your selected sitter is not available`,
  [HeaderTitlesEnum.BEST_MATCHES]: t`Best matches for your request`,
});

// SUBHEADERS
export enum SubHeadersEnum {
  ADD_DATES,
  SHOWING_OTHER_PROVIDERS,
  SEEING_PROVIDERS_DATES,
  BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME,
  BEST_MATCH_LIST_SHORT_LEAD_TIME,
  SELECTED_SITTER_NO_DATES,
  SELECTED_SITTER_AVAILABLE,
  SELECTED_SITTER_UNAVAILABLE,
  POST_SELECTED_SITTER_RESULTS,
}

const getAddDatesSubHeader = (serviceType?: ServiceType, limitTopResults?: boolean): string => {
  if (!serviceType) {
    return '';
  }
  if (limitTopResults) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`These sitters are likely to fit your pet's needs and accept requests. Add dates to see if these boarding sitters are available.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`These sitters are likely to fit your pet's needs and accept requests. Add dates to see if these daycare sitters are available.`;
      case ServiceType.DROP_IN:
        return t`These sitters are likely to fit your pet's needs and accept requests. Add dates to see if these drop-in sitters are available.`;
      case ServiceType.DOG_WALKING:
        return t`These dog walkers are likely to fit your pet's needs and accept requests. Add dates to see if these dog walkers are available.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`These sitters are likely to fit your pet's needs and accept requests. Add dates to see if these sitters are available.`;
    }
  } else {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Add dates to see boarding sitters who'll be available for your need. These are sitters in your area, but they might not be available.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Add dates to see daycare sitters who'll be available for your need. These are sitters in your area, but they might not be available.`;
      case ServiceType.DROP_IN:
        return t`Add dates to see drop-in sitters who'll be available for your need. These are sitters in your area, but they might not be available.`;
      case ServiceType.DOG_WALKING:
        return t`Add dates to see dog walkers who'll be available for your need. These are dog walkers in your area, but they might not be available.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Add dates to see sitters who'll be available for your need. These are sitters in your area, but they might not be available.`;
    }
  }
};

const getShowingOtherProvidersSubHeader = (serviceType?: ServiceType): string => {
  if (!serviceType) {
    return '';
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
      return t`Showing other available boarding sitters.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Showing other available daycare sitters.`;
    case ServiceType.DROP_IN:
      return t`Showing other available drop-in sitters.`;
    case ServiceType.DOG_WALKING:
      return t`Showing other available dog walkers.`;
    case ServiceType.OVERNIGHT_TRAVELING:
    default:
      return t`Showing other available sitters.`;
  }
};

const getYoureSeeingProviders = (
  serviceType?: ServiceType,
  dateData?: DateData,
  limitTopResults?: boolean
): string => {
  if (!serviceType || !dateData) {
    return '';
  }

  const { isSameDate, startDate, endDate } = dateData || {};
  if (limitTopResults) {
    if (isSameDate) {
      switch (serviceType) {
        case ServiceType.OVERNIGHT_BOARDING:
          return t`These boarding sitters are likely to fit your pet's needs, accept requests, and are available on **${startDate}**.`;
        case ServiceType.DOGGY_DAY_CARE:
          return t`These daycare sitters are likely to fit your pet's needs, accept requests, and are available on **${startDate}**.`;
        case ServiceType.DROP_IN:
          return t`These drop-in sitters are likely to fit your pet's needs, accept requests, and are available on **${startDate}**.`;
        case ServiceType.DOG_WALKING:
          return t`These dog walkers are likely to fit your pet's needs, accept requests, and are available on **${startDate}**.`;
        case ServiceType.OVERNIGHT_TRAVELING:
        default:
          return t`These sitters are likely to fit your pet's needs, accept requests, and are available on **${startDate}**.`;
      }
    }

    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`These boarding sitters are likely to fit your pet's needs, accept requests, and are available from **${startDate} to ${endDate}**.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`These daycare sitters are likely to fit your pet's needs, accept requests, and are available from **${startDate} to ${endDate}**.`;
      case ServiceType.DROP_IN:
        return t`These drop-in sitters are likely to fit your pet's needs, accept requests, and are available from **${startDate} to ${endDate}**.`;
      case ServiceType.DOG_WALKING:
        return t`These dog walkers are likely to fit your pet's needs, accept requests, and are available from **${startDate} to ${endDate}**.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`These sitters are likely to fit your pet's needs, accept requests, and are available from **${startDate} to ${endDate}**.`;
    }
  } else {
    if (isSameDate) {
      switch (serviceType) {
        case ServiceType.OVERNIGHT_BOARDING:
          return t`You're seeing boarding sitters available on **${startDate}**.`;
        case ServiceType.DOGGY_DAY_CARE:
          return t`You're seeing daycare sitters available on **${startDate}**.`;
        case ServiceType.DROP_IN:
          return t`You're seeing drop-in sitters available on **${startDate}**.`;
        case ServiceType.DOG_WALKING:
          return t`You're seeing dog walkers available on **${startDate}**.`;
        case ServiceType.OVERNIGHT_TRAVELING:
        default:
          return t`You're seeing sitters available on **${startDate}**.`;
      }
    }

    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`You're seeing boarding sitters available from **${startDate} to ${endDate}**.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`You're seeing daycare sitters available from **${startDate} to ${endDate}**.`;
      case ServiceType.DROP_IN:
        return t`You're seeing drop-in sitters available from **${startDate} to ${endDate}**.`;
      case ServiceType.DOG_WALKING:
        return t`You're seeing dog walkers available from **${startDate} to ${endDate}**.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`You're seeing sitters available from **${startDate} to ${endDate}**.`;
    }
  }
};

const getSelectedSitterAvailable = (sitterName?: string, dateData?: DateData): string => {
  if (!sitterName || !dateData) {
    return '';
  }

  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    return t`${sitterName} is available on **${startDate}**.`;
  }

  return t`${sitterName} is available from **${startDate} to ${endDate}**.`;
};

const getSelectedSitterUnavailable = (sitterName?: string, dateData?: DateData): string => {
  if (!sitterName || !dateData) {
    return '';
  }

  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    return t`${sitterName} is not available on **${startDate}**.`;
  }

  return t`${sitterName} is not available from **${startDate} to ${endDate}**.`;
};

const getPostSelectedSitterResults = (serviceType?: ServiceType, dateData?: DateData): string => {
  if (!serviceType || !dateData) {
    return '';
  }

  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Showing boarding sitters available on **${startDate}**.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Showing daycare sitters available on **${startDate}**.`;
      case ServiceType.DROP_IN:
        return t`Showing drop-in sitters available on **${startDate}**.`;
      case ServiceType.DOG_WALKING:
        return t`Showing dog walkers available on **${startDate}**.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Showing sitters available on **${startDate}**.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
      return t`Showing boarding sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Showing daycare sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DROP_IN:
      return t`Showing drop-in sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DOG_WALKING:
      return t`Showing dog walkers available from **${startDate} to ${endDate}**.`;
    case ServiceType.OVERNIGHT_TRAVELING:
    default:
      return t`Showing sitters available from **${startDate} to ${endDate}**.`;
  }
};

export const getSubHeader = (
  subHeader: SubHeadersEnum,
  params: {
    serviceType?: ServiceType;
    sitterName?: string;
    dateData?: DateData;
    limitTopResults?: boolean;
  } = {}
): string => {
  const { serviceType, sitterName, dateData, limitTopResults } = params;
  switch (subHeader) {
    case SubHeadersEnum.ADD_DATES:
      return getAddDatesSubHeader(serviceType, limitTopResults);
    case SubHeadersEnum.SHOWING_OTHER_PROVIDERS:
      return getShowingOtherProvidersSubHeader(serviceType);
    case SubHeadersEnum.SEEING_PROVIDERS_DATES:
      return getYoureSeeingProviders(serviceType, dateData, limitTopResults);
    case SubHeadersEnum.SELECTED_SITTER_NO_DATES:
      if (serviceType === ServiceType.DOG_WALKING) {
        return t`Add dates to see if this dog walker is available.`;
      }
      return t`Add dates to see if this sitter is available.`;
    case SubHeadersEnum.SELECTED_SITTER_AVAILABLE:
      return getSelectedSitterAvailable(sitterName, dateData);
    case SubHeadersEnum.SELECTED_SITTER_UNAVAILABLE:
      return getSelectedSitterUnavailable(sitterName, dateData);
    case SubHeadersEnum.POST_SELECTED_SITTER_RESULTS:
      return getPostSelectedSitterResults(serviceType, dateData);
    case SubHeadersEnum.BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME:
      if (serviceType === 'dog-walking') {
        return t`This dog walker accepts last-minute requests.`;
      }
      return t`This sitter accepts last-minute requests.`;
    case SubHeadersEnum.BEST_MATCH_LIST_SHORT_LEAD_TIME:
      if (serviceType === 'dog-walking') {
        return t`These dog walkers accept last-minute requests.`;
      }
      return t`These sitters accept last-minute requests.`;
    default:
      return '';
  }
};

// ALERTS - Price Transparency
export enum AlertContentEnum {
  PRICE_TRANSPARENCY_FULL_ADD_DATES,
  PRICE_TRANSPARENCY_LITE_ADD_DATES,
  PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT,
  PRICE_TRANSPARENCY_LITE_PRICES_FOR_UNIT,
  PRICE_TRANSPARENCY_FULL_SEO_PAGE,
  PRICE_TRANSPARENCY_LITE_SEO_PAGE,
  PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE,
  PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_AVAILABLE,
  PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE,
  PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_UNAVAILABLE,
  PRICE_TRANSPARENCY_LITE_ADD_DATES_AND_PETS,
  PRICE_TRANSPARENCY_LITE_ADD_PETS,
}

const getPriceTransparencyFullAddDatesAlertContent = (
  serviceType: ServiceType,
  individualPetTypeCount?: string,
  bookingFeePercentage?: string
): string => {
  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Add your dates to get accurate pricing. Prices are estimates per night for ${individualPetTypeCount}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Add your dates to get accurate pricing. Prices are estimates per day for ${individualPetTypeCount}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Add your dates to get accurate pricing. Prices are estimates per visit for ${individualPetTypeCount}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Add your dates to get accurate pricing. Prices are estimates per walk for ${individualPetTypeCount}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyLiteAddDatesAlertContent = (
  serviceType: ServiceType,
  bookingFeePercentage?: string
): string => {
  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Add your dates to get accurate pricing. Prices are estimates per night. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Add your dates to get accurate pricing. Prices are estimates per day. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      return t`Add your dates to get accurate pricing. Prices are estimates per visit. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`Add your dates to get accurate pricing. Prices are estimates per walk. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyFullSeoPageAlertContent = (
  serviceType: ServiceType,
  bookingFeePercentage?: string,
  petType: PetType = PetType.DOG
): string => {
  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Prices are estimates per night for 1 dog. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Prices are estimates per day for 1 dog. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      switch (petType) {
        case PetType.CAT:
          return t`Prices are estimates per visit for 1 cat. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
        case PetType.DOG:
        default:
          return t`Prices are estimates per visit for 1 dog. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      }
    case ServiceType.DOG_WALKING:
    default:
      return t`Prices are estimates per walk for 1 dog. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyLiteSeoPageAlertContent = (
  serviceType: ServiceType,
  bookingFeePercentage?: string
): string => {
  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Prices are estimates per night. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Prices are estimates per day. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      return t`Prices are estimates per visit. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`Prices are estimates per walk. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyFullResultsListAlertContent = (
  serviceType: ServiceType,
  individualPetTypeCount?: string,
  dateData?: DateData,
  bookingFeePercentage?: string
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per night for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per day for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per visit for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per walk for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Prices are per night for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Prices are per day for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Prices are per visit for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`Prices are per walk for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyLiteResultsListAlertContent = (
  serviceType: ServiceType,
  dateData?: DateData,
  bookingFeePercentage?: string
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`Prices are per night from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Prices are per day from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        return t`Prices are per visit from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        return t`Prices are per walk from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Prices are per night from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Prices are per day from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      return t`Prices are per visit from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`Prices are per walk from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyFullSelectedSitterAvailableAlertContent = (
  serviceType: ServiceType,
  sitterName?: string,
  individualPetTypeCount?: string,
  dateData?: DateData,
  bookingFeePercentage?: string
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is available. Prices are per night for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is available. Prices are per day for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is available. Prices are per visit for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is available. Prices are per walk for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is available. Prices are per night for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is available. Prices are per day for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is available. Prices are per visit for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is available. Prices are per walk for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyLiteSelectedSitterAvailableAlertContent = (
  serviceType: ServiceType,
  sitterName?: string,
  bookingFeePercentage?: string,
  dateData?: DateData
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`${sitterName} is available. Prices are per night from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`${sitterName} is available. Prices are per day from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        return t`${sitterName} is available. Prices are per visit from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        return t`${sitterName} is available. Prices are per walk from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`${sitterName} is available. Prices are per night from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`${sitterName} is available. Prices are per day from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      return t`${sitterName} is available. Prices are per visit from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`${sitterName} is available. Prices are per walk from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyFullSelectedSitterUnavailableAlertContent = (
  serviceType: ServiceType,
  sitterName?: string,
  individualPetTypeCount?: string,
  dateData?: DateData,
  bookingFeePercentage?: string
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is not available. Prices are per night for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is not available. Prices are per day for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is not available. Prices are per visit for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`${sitterName} is not available. Prices are per walk for ${individualPetTypeCount} on ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is not available. Prices are per night for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is not available. Prices are per day for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is not available. Prices are per visit for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      /* i18n: {individualPetTypeCount} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
      return t`${sitterName} is not available. Prices are per walk for ${individualPetTypeCount} from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

const getPriceTransparencyLiteSelectedSitterUnavailableAlertContent = (
  serviceType: ServiceType,
  sitterName?: string,
  bookingFeePercentage?: string,
  dateData?: DateData
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`${sitterName} is not available. Prices are per night from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`${sitterName} is not available. Prices are per day from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DROP_IN:
        return t`${sitterName} is not available. Prices are per visit from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
      case ServiceType.DOG_WALKING:
      default:
        return t`${sitterName} is not available. Prices are per walk from ${startDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`${sitterName} is not available. Prices are per night from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`${sitterName} is not available. Prices are per day from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DROP_IN:
      return t`${sitterName} is not available. Prices are per visit from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`${sitterName} is not available. Prices are per walk from ${startDate} to ${endDate}. Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
};

export const getLiteAlertContentType = (
  hasDates: boolean,
  petCount: number,
  isPetCountersExperiment?: boolean
): AlertContentEnum => {
  if (isPetCountersExperiment) {
    if (!hasDates && petCount === 0) {
      return AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_DATES_AND_PETS;
    }
    if (hasDates && petCount === 0) {
      return AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_PETS;
    }
  }
  if (hasDates) {
    return AlertContentEnum.PRICE_TRANSPARENCY_LITE_PRICES_FOR_UNIT;
  }
  return AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_DATES;
};

export const getPriceTransparencyLiteAddDatesAndPetsAlertContent = (
  serviceType: ServiceType
): string => {
  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Add your dates and pets to get accurate pricing. Prices are estimates per night. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Add your dates and pets to get accurate pricing. Prices are estimates per day. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DROP_IN:
      return t`Add your dates and pets to get accurate pricing. Prices are estimates per visit. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`Add your dates and pets to get accurate pricing. Prices are estimates per walk. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
  }
};

export const getPriceTransparencyLiteAddPetsAlertContent = (
  serviceType: ServiceType,
  dateData?: DateData
): string => {
  const { isSameDate, startDate, endDate } = dateData || {};

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`Add your pets to get accurate pricing. Prices are estimates per night from ${startDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Add your pets to get accurate pricing. Prices are estimates per day from ${startDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
      case ServiceType.DROP_IN:
        return t`Add your pets to get accurate pricing. Prices are estimates per visit from ${startDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
      case ServiceType.DOG_WALKING:
      default:
        return t`Add your pets to get accurate pricing. Prices are estimates per walk from ${startDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
    case ServiceType.OVERNIGHT_TRAVELING:
      return t`Add your pets to get accurate pricing. Prices are estimates per night from ${startDate} to ${endDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Add your pets to get accurate pricing. Prices are estimates per day from ${startDate} to ${endDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DROP_IN:
      return t`Add your pets to get accurate pricing. Prices are estimates per visit from ${startDate} to ${endDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
    case ServiceType.DOG_WALKING:
    default:
      return t`Add your pets to get accurate pricing. Prices are estimates per walk from ${startDate} to ${endDate}. A booking fee of 11% and taxes will apply. Pricing may vary based on your search.`;
  }
};

export const getAlertContent = (
  alertContent: AlertContentEnum,
  params: {
    dateData?: DateData;
    bookingFeePercentage?: string;
    individualPetTypeCountDescription?: string;
    serviceType: ServiceType;
    sitterName?: string;
    petType?: PetType;
  }
): string => {
  const {
    serviceType,
    sitterName,
    dateData,
    individualPetTypeCountDescription,
    bookingFeePercentage,
    petType,
  } = params;

  switch (alertContent) {
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES:
      return getPriceTransparencyFullAddDatesAlertContent(
        serviceType,
        individualPetTypeCountDescription,
        bookingFeePercentage
      );

    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_DATES:
      return getPriceTransparencyLiteAddDatesAlertContent(serviceType, bookingFeePercentage);
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT:
      return getPriceTransparencyFullResultsListAlertContent(
        serviceType,
        individualPetTypeCountDescription,
        dateData,
        bookingFeePercentage
      );

    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_PRICES_FOR_UNIT:
      return getPriceTransparencyLiteResultsListAlertContent(
        serviceType,
        dateData,
        bookingFeePercentage
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SEO_PAGE:
      return getPriceTransparencyFullSeoPageAlertContent(
        serviceType,
        bookingFeePercentage,
        petType
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_SEO_PAGE:
      return getPriceTransparencyLiteSeoPageAlertContent(serviceType, bookingFeePercentage);
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE:
      return getPriceTransparencyFullSelectedSitterAvailableAlertContent(
        serviceType,
        sitterName,
        individualPetTypeCountDescription,
        dateData,
        bookingFeePercentage
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_AVAILABLE:
      return getPriceTransparencyLiteSelectedSitterAvailableAlertContent(
        serviceType,
        sitterName,
        bookingFeePercentage,
        dateData
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE:
      return getPriceTransparencyFullSelectedSitterUnavailableAlertContent(
        serviceType,
        sitterName,
        individualPetTypeCountDescription,
        dateData,
        bookingFeePercentage
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_UNAVAILABLE:
      return getPriceTransparencyLiteSelectedSitterUnavailableAlertContent(
        serviceType,
        sitterName,
        bookingFeePercentage,
        dateData
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_DATES_AND_PETS:
      return getPriceTransparencyLiteAddDatesAndPetsAlertContent(serviceType);
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_PETS:
      return getPriceTransparencyLiteAddPetsAlertContent(serviceType, dateData);
    default:
      return '';
  }
};
